import { useEffect, useState } from 'react';
import Image from 'next/image';
import type { CartLineT } from '@lambda/apis/src/cart/types';
import type { OfferT } from '@lambda/apis/src/catalog-v2/types';
import { Dialog } from '@/components/commons/Dialog';
import Loading from '@/components/commons/Loading';
import { priceFormater } from '@/settings';
import { priceFormatSuperscript } from '@/lib/price';
import i18n from '@/settings/i18n';
import OrderDiscountBadge from '@/components/order-discounts/OrderDiscountBadge';
import useOrderDiscounts from '@/lib/order-discounts/useOrderDiscounts';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { formatDeliveryDates } from './helpers';
import { allocateQuantity } from './common/offerAllocation';
import QuantitySelector from './common/QuantitySelector';
import { splitProductTitle } from '../product/sections/main/right/product/helpers';

const t = i18n.init('cart');

const totalStock = (offers: OfferT[]) =>
  offers.reduce((total, offer) => total + (offer.stock ?? 0), 0);

const deliveryFor = (offer: OfferT) => {
  const standard = offer.deliveryEstimates?.find(
    (estimate) => estimate.type === 'standard',
  );

  if (!standard) {
    return '';
  }

  return formatDeliveryDates({
    minDeliveryDate: standard.minDate,
    maxDeliveryDate: standard.maxDate,
  });
};

/**
 * Shown when a quantity cannot come from one seller. Spreading an order across
 * vendors means several deliveries at several unit prices, so the split is put
 * to the shopper before the cart is changed rather than after.
 */
const OfferSplitModal = ({
  showModal,
  setShowModal,
  line,
  otherLinesSubtotal,
  offers,
  quantity,
  loading,
  onConfirm,
}: {
  showModal: boolean;
  setShowModal: (open: boolean) => void;
  line: CartLineT;
  /** Device subtotal of the rest of the cart, in minor units. */
  otherLinesSubtotal: number;
  offers: OfferT[];
  quantity: number;
  loading: boolean;
  onConfirm: (quantity: number) => void;
}) => {
  const [selected, setSelected] = useState(quantity);

  // Reopening for a different quantity should start from that quantity.
  useEffect(() => setSelected(quantity), [quantity, showModal]);

  const available = totalStock(offers);
  const { primaryTitle, secondaryTitle } = splitProductTitle(line.title ?? '');

  // The compare-at note is per unit, so it scales with what is being ordered.
  const comparePrice = line.notes?.find(
    (note) => note.name === '_compare_price',
  )?.value;

  const split = allocateQuantity(offers, selected);
  const total = split.reduce((sum, share) => {
    const offer = offers.find((o) => o.id === share.offerId);

    return sum + (offer?.price ?? 0) * share.quantity;
  }, 0);

  const comparedTo = comparePrice ? Number(comparePrice) * selected : null;

  // Same internal-only gate as the cart summary's badge.
  const { customer } = useCustomerContext();
  const orderDiscount = useOrderDiscounts(
    otherLinesSubtotal + total,
    customer?.email,
  );

  return (
    <Dialog open={showModal} onClose={() => setShowModal(false)}>
      <h2 className="pr-6 text-base font-bold">
        {t`Total ${{
          count: available,
        }} units in stock across certified sellers:`}
      </h2>

      {/* No `overflow-hidden`: it would clip the quantity dropdown when it
          opens. The gradient rounds its own top corners instead. */}
      <div className="mt-4 rounded-xl border">
        <div className="flex items-start gap-3 rounded-t-xl bg-[linear-gradient(104deg,_#FFF_0%,_#F7F8FA_100%)] p-4">
          <div className="h-12 w-12 shrink-0">
            <Image
              src={line.image ?? ''}
              alt={line.title ?? ''}
              width={64}
              height={64}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-bold">{primaryTitle}</div>
            {secondaryTitle && (
              <div className="text-xs text-gray-700/75">{secondaryTitle}</div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-baseline gap-2">
              {comparedTo != null && comparedTo > total / 100 && (
                <span
                  className="text-xs text-[#99A1AF] line-through xxs:text-sm"
                  translate="no"
                >
                  {priceFormatSuperscript(priceFormater.long(comparedTo))}
                </span>
              )}
              <span className="text-base font-bold" translate="no">
                {priceFormatSuperscript(priceFormater.long(total / 100))}
              </span>
            </div>
            <OrderDiscountBadge state={orderDiscount} />
            <QuantitySelector
              quantity={selected}
              max={available}
              disabled={loading}
              onChange={setSelected}
            />
          </div>
        </div>

        <ul className="divide-y border-t text-xs">
          {offers.map((offer) => (
            <li
              key={offer.id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
            >
              <span className="text-gray-700/60">
                {t`${{ stock: offer.stock }} in stock for`}{' '}
                <span className="font-medium text-gray-700" translate="no">
                  {priceFormatSuperscript(
                    priceFormater.short(offer.price / 100),
                  )}
                </span>{' '}
                {t`per unit`}
              </span>
              <span className="text-gray-700/60">
                {t`FREE Delivery`} {deliveryFor(offer)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-gray-700 font-bold text-white duration-200 ease-in-out hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        disabled={loading}
        onClick={() => onConfirm(selected)}
      >
        {loading ? <Loading size="xs" /> : t`Update cart`}
      </button>
    </Dialog>
  );
};

export default OfferSplitModal;
